import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useJsonLd, instructorSchema, breadcrumbSchema } from "@/hooks/useJsonLd";
import {
  Flame, Award, GraduationCap, Wrench, Building2, Zap, ShieldCheck, ArrowRight,
  CheckCircle2, MapPin, Briefcase, ArrowLeft,
} from "lucide-react";

const NOTABLE_PROJECTS = [
  { name: "World Trade Center", city: "NYC", role: "Service, troubleshooting & inspection" },
  { name: "Hudson Yards", city: "NYC", role: "Service, troubleshooting & inspection" },
  { name: "JPMorgan Chase Headquarters", city: "NYC", role: "Service, troubleshooting & inspection" },
  { name: "Federal Reserve Bank of New York", city: "NYC", role: "Service, troubleshooting & inspection" },
  { name: "JFK International Airport", city: "NYC", role: "Fire Alarm Safety Manager" },
  { name: "L'Oréal Corporate", city: "NYC", role: "Service, troubleshooting & inspection" },
  { name: "New York University (NYU)", city: "NYC", role: "Inspection, maintenance, system support" },
  { name: "John Jay College of Criminal Justice", city: "NYC", role: "Inspection, maintenance, system support" },
  { name: "York College", city: "NYC", role: "Inspection, maintenance, system support" },
  { name: "North Shore University Hospital", city: "Long Island", role: "Early-career system installation" },
  { name: "Walmart (Northeast US)", city: "Multi-state", role: "Regional fire alarm inspection program" },
];

const CERTIFICATIONS = [
  { code: "S-95", name: "Supervision of Fire Alarm Systems (Visual Inspection, No Voice)" },
  { code: "S-98", name: "Supervision of Fire Alarm Systems & Other Related Systems" },
  { code: "S-12", name: "Citywide Sprinkler Systems" },
  { code: "S-13", name: "Citywide Standpipe Systems" },
  { code: "F-53", name: "Fire Alarm Systems w/ One-Way Voice Communication" },
  { code: "OSHA 30", name: "Construction Safety & Health (30-hour)" },
];

const SPECIALIZATIONS = [
  { i: Wrench, t: "Audio / voice evacuation fault diagnosis", d: "EVACS amplifier troubleshooting, speaker circuit fault isolation, and multi-zone paging system service." },
  { i: Zap, t: "Circuit-level troubleshooting", d: "SLC loop ground faults, NAC circuit shorts, addressable device communication failures on large-scale systems." },
  { i: ShieldCheck, t: "Special hazard suppression coordination", d: "FM-200 clean-agent systems, pre-action systems, and the integration between fire alarm, sprinkler, and mechanical disciplines." },
  { i: CheckCircle2, t: "FDNY inspection & compliance", d: "100+ FDNY inspections completed for new building openings, business establishments, and ongoing compliance audits across NYC." },
];

export default function InstructorBio() {
  useDocumentMeta({
    title: "Meet Your Instructor — Trevor Marson",
    description: "15+ years of NYC fire-alarm field experience — WTC, Hudson Yards, JPMorgan Chase, NYU, Federal Reserve, John Jay College. IBEW Local 3 H-Division. FDNY S-95, S-98, S-13, F-53 + OSHA 30.",
    image: "/og/og-bio.png",
    path: "/about-instructor",
  });
  useJsonLd([
    instructorSchema(),
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "About the Instructor", url: "/about-instructor" },
    ]),
  ]);

  return (
    <div className="bg-white">
      {/* Minimal nav */}
      <header className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="bio-logo">
            <div className="w-7 h-7 bg-red-600 grid place-items-center">
              <Flame className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-heading text-[14px] font-bold tracking-tight">APM Fire Alarm</div>
          </Link>
          <div className="flex items-center gap-5">
            <Link to="/start" className="text-xs font-mono uppercase tracking-wider text-zinc-600 hover:text-red-600" data-testid="bio-funnel-link">Enroll</Link>
            <Link to="/login" className="text-xs font-mono uppercase tracking-wider text-zinc-600 hover:text-red-600" data-testid="bio-login-link">Log in</Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid="instructor-bio-page">
        <Link to="/start" className="text-sm text-zinc-600 hover:text-red-600 inline-flex items-center gap-1 mb-6" data-testid="bio-back">
          <ArrowLeft className="w-4 h-4" /> Back to enrollment
        </Link>

        {/* HERO */}
        <section className="grid lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="aspect-square bg-zinc-950 border-4 border-red-600 grid place-items-center">
                <div className="text-center px-6">
                  <div className="w-24 h-24 rounded-full bg-red-600 grid place-items-center mx-auto mb-4">
                    <span className="font-heading text-4xl font-bold tracking-tighter text-white">TM</span>
                  </div>
                  <div className="font-heading text-2xl font-bold tracking-tight text-white">Trevor Marson</div>
                  <div className="text-xs font-mono uppercase tracking-widest text-zinc-400 mt-1">Lead Instructor</div>
                </div>
              </div>
              <div className="absolute -top-3 -right-3 bg-red-600 text-white px-3 py-1.5 font-mono text-xs uppercase tracking-widest font-bold transform rotate-3">
                IBEW Local 3
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="overline mb-3"><span className="red-bar" />Meet your instructor</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.95] mb-5" data-testid="bio-headline">
              15+ years in the field.<br />
              <span className="text-red-600">From WTC to JFK.</span>
            </h1>
            <p className="text-lg text-zinc-700 leading-relaxed mb-6">
              Trevor Marson is a NYC fire alarm technician whose résumé reads like a tour of the most
              critical buildings in the country — the <strong>World Trade Center</strong>,
              <strong> Hudson Yards</strong>, <strong>JPMorgan Chase Headquarters</strong>, the
              <strong> Federal Reserve Bank of New York</strong>, and the
              <strong> NYU / John Jay / York</strong> campuses.
            </p>
            <p className="text-base text-zinc-700 leading-relaxed mb-6">
              He's run 100+ FDNY acceptance inspections, supervised pre-action and FM-200 suppression
              installs, and led regional inspection programs for Walmart across the Northeast. He's a
              proud member of <strong>IBEW Local 3 (H Division)</strong>. Every lesson in this course
              is grounded in what he's actually seen on the job.
            </p>
            <div className="flex gap-3">
              <Link to="/start" data-testid="bio-cta-enroll">
                <button className="btn-primary">
                  Enroll for $149 <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
              <Link to="/sample" data-testid="bio-cta-sample">
                <button className="btn-outline">Try a free lesson</button>
              </Link>
            </div>
          </div>
        </section>

        {/* CREDENTIALS STRIP */}
        <section className="mb-16" data-testid="bio-credentials">
          <div className="overline mb-4"><span className="red-bar" />Certifications</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {CERTIFICATIONS.map((c) => (
              <div key={c.code} className="card-tech p-4 flex items-start gap-3">
                <div className="w-10 h-10 bg-red-600 text-white grid place-items-center flex-shrink-0">
                  <Award className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <div>
                  <div className="font-heading text-lg font-bold tracking-tight">{c.code}</div>
                  <div className="text-xs text-zinc-600 leading-relaxed">{c.name}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CAREER NARRATIVE */}
        <section className="mb-16">
          <div className="overline mb-4"><span className="red-bar" />Career overview</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter leading-tight mb-6">
            From healthcare installs to the most secure buildings in America.
          </h2>

          <div className="space-y-5 text-zinc-700 leading-relaxed">
            <p>
              Trevor began his career in <strong>fire alarm system installation</strong>, working in major
              healthcare facilities across Long Island including projects with North Shore University Hospital.
              That foundation gave him a working knowledge of system design, installation practices, and
              code-compliant execution from the wire up.
            </p>
            <p>
              He advanced into <strong>inspection and compliance work</strong> throughout New York City, conducting
              over <strong>100 FDNY inspections</strong> for new building openings and business establishments.
              He has also served as the <strong>Fire Alarm Safety Manager at JFK International Airport</strong>,
              overseeing fire alarm system reliability across terminal operations. His scope covers full system
              testing, device verification, documentation, and ensuring compliance with NYC Fire Code, the
              Building Code, and FDNY rules.
            </p>
            <p>
              In addition to fire alarm, Trevor has worked extensively within the <strong>sprinkler and
              special hazards sector</strong>, overseeing inspections and maintenance of pre-action systems
              and clean-agent suppression including the <strong>FM-200 Fire Suppression System</strong>.
              His ability to coordinate between fire alarm, sprinkler, and mechanical disciplines makes him
              uniquely effective in complex, multi-system buildings.
            </p>
            <p>
              On a regional level, Trevor has supported fire alarm inspection programs for
              <strong> Walmart locations throughout the Northeastern United States</strong>, conducting
              inspections and ensuring system reliability across multiple sites.
            </p>
            <p>
              He's a proud member of <strong>IBEW Local 3 (H Division)</strong>, where his electrical and
              field expertise has been forged. Known for his methodical approach and attention to detail,
              he specializes in diagnosing complex system issues — audio/voice evacuation faults, circuit
              shorts, and addressable device failures on large-scale systems.
            </p>
          </div>
        </section>

        {/* NOTABLE PROJECTS GRID */}
        <section className="mb-16">
          <div className="overline mb-4"><span className="red-bar" />Where he's worked</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter leading-tight mb-2">
            The buildings that demand the highest standards.
          </h2>
          <p className="text-zinc-700 mb-6 max-w-2xl leading-relaxed">
            Every project below required mission-critical reliability, complex multi-system coordination,
            and absolutely no margin for error. These are the experiences that shape every lesson in the course.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="bio-projects">
            {NOTABLE_PROJECTS.map((p) => (
              <div key={p.name} className="card-tech p-4 hover:border-red-600 transition-colors">
                <div className="flex items-start gap-2.5">
                  <Building2 className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="font-heading text-base font-bold tracking-tight">{p.name}</div>
                    <div className="text-[11px] font-mono text-zinc-500 uppercase tracking-wider mt-0.5">
                      <MapPin className="w-3 h-3 inline mr-1" />{p.city}
                    </div>
                    <div className="text-xs text-zinc-700 mt-1.5 leading-snug">{p.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* SPECIALIZATIONS */}
        <section className="mb-16">
          <div className="overline mb-4"><span className="red-bar" />Specializations</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter leading-tight mb-6">
            Mission-critical troubleshooting & inspection.
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {SPECIALIZATIONS.map((s) => (
              <div key={s.t} className="card-tech p-5">
                <div className="w-10 h-10 bg-red-600 text-white grid place-items-center mb-3">
                  <s.i className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <div className="font-heading text-lg font-bold tracking-tight mb-1.5">{s.t}</div>
                <p className="text-sm text-zinc-700 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CLOSING / CTA */}
        <section className="bg-zinc-950 text-white p-8 sm:p-12 -mx-6 sm:mx-0">
          <div className="grid lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-8">
              <div className="overline text-zinc-500 mb-3"><span className="red-bar" />Why learn from Trevor</div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter leading-tight mb-4">
                You're learning from someone who's been on the wire.
              </h2>
              <p className="text-zinc-300 leading-relaxed">
                Trevor remains committed to delivering safe, efficient, code-compliant life safety solutions —
                with a focus on system integrity, accuracy in reporting, and long-term system reliability.
                Every lesson in this course is grounded in real installations, real inspections, and real
                troubleshooting from the most demanding buildings in NYC and the Northeast.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-2">
              <Link to="/start" data-testid="bio-cta-final-enroll">
                <button className="btn-primary w-full justify-center">
                  Enroll for $149 <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
              <Link to="/sample" data-testid="bio-cta-final-sample">
                <button className="border-2 border-zinc-700 hover:border-white text-white px-5 py-3 font-semibold inline-flex items-center justify-center gap-2 text-sm transition-colors w-full">
                  <GraduationCap className="w-4 h-4" /> Try a free lesson
                </button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-zinc-950 text-zinc-500 py-10 px-6 sm:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-mono">
          <div className="flex items-center gap-2">
            <Flame className="w-4 h-4 text-red-600" />
            <span>© APM Fire Alarm Exam Prep</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/" className="hover:text-zinc-100">Home</Link>
            <Link to="/start" className="hover:text-zinc-100">Enroll</Link>
            <Link to="/sample" className="hover:text-zinc-100">Free lesson</Link>
            <Link to="/login" className="hover:text-zinc-100">Log in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
